import { LineGame } from '../../games/line/LineGame.js';
import { LineVortex } from '../../games/line/LineVortex.js';
import { LineRenderer } from '../../games/line/LineRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Line-specific types ──────────────────────────────────────────

export interface LineRenderOptions {
    // Placeholder for future fine-grained control
}

// ── Adapter ─────────────────────────────────────────────────────

export class LineTutorialAdapter implements TutorialGameAdapter {
    private game: LineGame;
    private renderer: LineRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new LineGame(grid);
        this.renderer = new LineRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const serialized = state as string;
        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createLineTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new LineTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface LineVortexTutorialState {
    line: string;
    gateMode?: boolean;
}

// ── Vortex Adapter ───────────────────────────────────────────────

export class LineVortexTutorialAdapter implements TutorialGameAdapter {
    private game: LineVortex;
    private renderer: LineRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new LineVortex(grid);
        this.renderer = new LineRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as LineVortexTutorialState | null;
        this.game.reset();
        this.game.setVortexData(this.gatePairs, this.allGates);

        if (!s) return;

        if (s.line) {
            this.game.deserialize(s.line);
        }

        this.currentGateMode = s.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createLineVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 4, col: 1 }, { row: 2, col: 2 }],  // B4 ↔ C2
        [{ row: 4, col: 3 }, { row: 1, col: 4 }],  // D4 ↔ E1
    ];
    return new LineVortexTutorialAdapter(canvas, grid, gatePairs);
}
